import type { MonitoringAlert } from '@bekebi/contracts/api';

/**
 * Alert presentation. Every value is the API's: state, reason, resolution and times. The browser
 * only words them and measures how long an alert lasted between the two recorded times.
 */

/** Why the alert opened, in the same words as service health. */
export const ALERT_REASON_TEXT: Record<MonitoringAlert['reason'], string> = {
  check_failing: 'A check is failing.',
  check_warning: 'A check reported a warning.',
};

/** How it ended: only by recovery. Unknown health never resolves an alert. */
export const ALERT_RESOLUTION_TEXT: Record<NonNullable<MonitoringAlert['resolution']>, string> = {
  recovered: 'The service recovered: its checks passed again.',
};

const BADGE = {
  failing: 'border-danger-line bg-danger-soft text-danger',
  warning: 'border-warning-line bg-warning-soft text-warning',
  resolved: 'border-line-strong bg-raised text-fg-2',
};

export function alertStateLabel(alert: MonitoringAlert): string {
  return alert.state === 'open' ? 'Open' : 'Resolved';
}

export function AlertStateBadge({ alert }: { alert: MonitoringAlert }) {
  const style =
    alert.state === 'resolved'
      ? BADGE.resolved
      : alert.reason === 'check_failing'
        ? BADGE.failing
        : BADGE.warning;
  const symbol = alert.state === 'open' ? '!' : '✓';
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${style}`}
    >
      <span aria-hidden="true">{symbol}</span>
      {alertStateLabel(alert)}
    </span>
  );
}

/** "12 minutes", "3 hours 5 minutes", "2 days 4 hours" — whole units, rounded down. */
export function formatDuration(fromIso: string, toIso: string): string {
  const minutes = Math.max(0, Math.floor((Date.parse(toIso) - Date.parse(fromIso)) / 60_000));
  const unit = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;
  if (minutes < 1) return 'less than a minute';
  if (minutes < 60) return unit(minutes, 'minute');
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return rest ? `${unit(hours, 'hour')} ${unit(rest, 'minute')}` : unit(hours, 'hour');
  }
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return rest ? `${unit(days, 'day')} ${unit(rest, 'hour')}` : unit(days, 'day');
}
